import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, isBefore, isSameDay, startOfDay } from "date-fns";
import { toZonedTime } from 'date-fns-tz'
import { timeZone } from "./constant";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function dateFormater({ date, formatString = "PPP" }: { date: Date, formatString?: string }): string {
  const zonedTime = toZonedTime(date, timeZone);
  return format(zonedTime, formatString);
}

export function timeFormatter({
  date,
  formatString = "hh:mm a",
}: {
  date: Date;
  formatString?: string;
}): string {
  const zonedTime = toZonedTime(date, timeZone);
  return format(zonedTime, formatString);
}

export function dateTimeFormatter({
  date,
  dateFormatString = "PPP",
  timeFormatString = "hh:mm a",
}: {
  date: Date;
  dateFormatString?: string;
  timeFormatString?: string;
}): string {
  const zonedTime = toZonedTime(date, timeZone);
  const formattedDate = format(zonedTime, dateFormatString);
  const formattedTime = format(zonedTime, timeFormatString);
  return `${formattedDate} ${formattedTime}`;
}

export function currencyHelper(price: number) {
  const priceStr = String(price);
  const priceChars = priceStr.split('');

  const formattedPrice = [];

  let count = 0;
  for (let i = priceChars.length - 1; i >= 0; i--) {
    formattedPrice.unshift(priceChars[i]);
    count++;
    if (count % 3 === 0 && i !== 0) {
      formattedPrice.unshift(',');
    }
  }

  return formattedPrice.join('');
}

export function formatRupee(amount: number) {
  const negative = amount < 0;
  const [whole = "0", fraction = "00"] = Math.abs(amount).toFixed(2).split(".");
  return `${negative ? "-" : ""}${currencyHelper(Number(whole))}.${fraction}`;
}

function getMonthYearKey(date: Date) {
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

export function groupDatesByMonthBook(availableDates: {
  id: string;
  start_date: Date;
  end_date: Date;
  price: number;
  expiration_date_time: string | null;
  batchId: string;
}[]) {
  const today = startOfDay(new Date()); // Get the start of today for accurate comparison
  const monthsMap: Record<string, typeof availableDates> = {}; // Map of month-year to array of dates

  // Iterate through each available date
  availableDates.forEach((date) => {
    const startDate = startOfDay(new Date(date.start_date)); // Get the start of the date for accurate comparison

    // Include today and future dates (today is no longer "before")
    if (!isBefore(startDate, today) || isSameDay(startDate, today)) {
      const monthYearKey = getMonthYearKey(startDate);

      // Initialize the array for the month if it doesn't exist, and add the date to that month
      if (!monthsMap[monthYearKey]) {
        monthsMap[monthYearKey] = [];
      }
      monthsMap[monthYearKey].push(date);
    }
  });

  // Sort the keys (month-year) chronologically and build the final sorted result
  const sortedResult = Object.keys(monthsMap)
    .sort((a, b) => {
      const dateA = new Date(`${a} 1`); // Create a valid date string for comparison
      const dateB = new Date(`${b} 1`);
      return dateA.getTime() - dateB.getTime();
    })
    .reduce((acc, key) => {
      if (monthsMap[key]) {
        acc[key] = monthsMap[key];
      }
      return acc;
    }, {} as Record<string, typeof availableDates>);

  return sortedResult;
}

const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];

const zero = "Zero";
const placeValues = [
  { value: 1e9, label: "Arab" },
  { value: 1e7, label: "Crore" },
  { value: 1e5, label: "Lakh" },
  { value: 1e3, label: "Thousand" },
  { value: 1e2, label: "Hundred" },
];

export function convertNumberToWords(amount: number) {
  if (amount === 0) return `${zero} Rupees Only`;

  function convert(num: number): string {
    if (num === 0) return zero;
    const parts: string[] = [];
    for (const { value, label } of placeValues) {
      const count = Math.floor(num / value);
      if (count > 0) {
        parts.push(`${convert(count)} ${label}`);
        num %= value;
      }
    }
    if (num >= 20) {
      parts.push(tens[Math.floor(num / 10)] ?? "");
      if (num % 10) parts.push(units[num % 10] ?? "");
    } else if (num >= 10) {
      parts.push(teens[num - 10] ?? "");
    } else if (num > 0) {
      parts.push(units[num] ?? "");
    }
    return parts.join(" ");
  }

  const integerPart = Math.floor(amount);
  const decimalPart = Math.round((amount - integerPart) * 100);
  let words = `${convert(integerPart)} Rupees`;
  if (decimalPart) words += ` and ${convert(decimalPart)} Paisa`;
  return `${words} Only`;
}

export function cloudFrontUrl(url: string): string {
  try {
    const parsedUrl = new URL(url);
    const path = parsedUrl.pathname; // Extracts "/adi1.jpg"
    return `https://d6jec12i0ml4j.cloudfront.net${path}`;
  } catch {
    console.error("Invalid URL provided:", url);
    // Fallback: return the original URL if it's not valid
    return url;
  }
}
